import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Plus, 
  TrendingUp, 
  TrendingDown,
  CreditCard, 
  Receipt,
  Edit3,
  Trash2, 
  PieChart,
  BarChart3,
  Calculator,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Download,
  Filter,
  Calendar,
  Wallet,
  Target,
  Activity
} from 'lucide-react';
import UniversalComments from './UniversalComments';
import CertificationTracking from './CertificationTracking';

const API = `${BACKEND_URL}/api`;

const FinancialManagement = ({ projectId, user }) => {
  const [budget, setBudget] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [financialReports, setFinancialReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);

  const [expenseFormData, setExpenseFormData] = useState({
    category: '',
    budget_category_id: '',
    description: '',
    amount: '',
    expense_type: 'operational',
    receipt_url: '',
    approval_status: 'pending'
  });

  const todayISO = new Date().toISOString().slice(0, 10);
  const [paymentFormData, setPaymentFormData] = useState({
    expense_id: '',
    recipient: '',
    amount: '',
    description: '',
    payment_method: 'bank_transfer',
    payment_date: todayISO,
    status: 'paid'
  });

  const [budgetFormData, setBudgetFormData] = useState({
    category: '',
    allocated_amount: '',
    period: 'monthly',
    description: ''
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Approval workflow states
  const [showExpenseApprovalDialog, setShowExpenseApprovalDialog] = useState(false);
  const [showPaymentApprovalDialog, setShowPaymentApprovalDialog] = useState(false);
  const [approvingExpense, setApprovingExpense] = useState(null);
  const [approvingPayment, setApprovingPayment] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Bulk selection states
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showBulkApprovalDialog, setShowBulkApprovalDialog] = useState(false);
  const [bulkApprovalType, setBulkApprovalType] = useState('');
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');

  useEffect(() => {
    fetchFinancialData();
  }, [projectId, selectedPeriod]);

  const fetchFinancialData = async () => {
    try {
      const [budgetRes, expensesRes, paymentsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/budget`),
        axios.get(`${API}/projects/${projectId}/expenses?period=${selectedPeriod}`),
        axios.get(`${API}/projects/${projectId}/payments?period=${selectedPeriod}`),
        axios.get(`${API}/projects/${projectId}/budget-categories`)
      ]);
      
      setBudget(budgetRes.data);
      setExpenses(expensesRes.data);
      setPayments(paymentsRes.data);
      setBudgetCategories(categoriesRes.data);
      
      // Calculate financial reports
      const totalExpenses = expensesRes.data.reduce((sum, exp) => sum + exp.amount, 0);
      const totalPayments = paymentsRes.data.reduce((sum, pay) => sum + pay.amount, 0);
      const budgetUtilization = budgetRes.data.total_budget > 0 ? 
        ((totalExpenses / budgetRes.data.total_budget) * 100).toFixed(1) : 0;
      
      setFinancialReports({
        totalExpenses,
        totalPayments,
        budgetUtilization,
        remainingBudget: budgetRes.data.total_budget - totalExpenses,
        expensesByCategory: groupExpensesByCategory(expensesRes.data),
        monthlyTrend: calculateMonthlyTrend(expensesRes.data)
      });
      
    } catch (error) {
      toast.error('Failed to load financial data');
      console.error('Financial data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupExpensesByCategory = (expenses) => {
    const grouped = expenses.reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
      return acc;
    }, {});
    
    return Object.entries(grouped).map(([category, amount]) => ({ category, amount }));
  };

  const calculateMonthlyTrend = (expenses) => {
    const monthlyData = {};
    expenses.forEach(expense => {
      const raw = expense.date || expense.created_at;
      if (!raw) return;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const month = d.toISOString().slice(0, 7); // YYYY-MM
      monthlyData[month] = (monthlyData[month] || 0) + (Number(expense.amount) || 0);
    });
    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({ month, amount }));
  };

  const exportFinancialReport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/reporting/export`,
        {
          format: exportFormat,
          title: 'Financial Report',
          project_id: projectId,
          sections: ['financial', 'payments'],
          report_id: 'financial_report',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const extMap = { pdf: 'pdf', xlsx: 'xlsx', csv: 'csv' };
      const ext = extMap[exportFormat] || 'pdf';
      const filename = `financial_report_${(projectId || 'all').slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Financial report exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export financial report');
    } finally {
      setExporting(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      const expenseData = {
        ...expenseFormData,
        amount: parseFloat(expenseFormData.amount),
        date: new Date().toISOString()
      };

      const token = localStorage.getItem('token');
      
      if (editingExpense) {
        // Update existing expense
        await axios.put(`${API}/projects/${projectId}/expenses/${editingExpense}`, expenseData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense updated successfully!');
        setEditingExpense(null);
      } else {
        // Create new expense
        await axios.post(`${API}/projects/${projectId}/expenses`, expenseData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense recorded successfully!');
      }
      
      setShowExpenseDialog(false);
      setExpenseFormData({
        category: '',
        budget_category_id: '',
        description: '',
        amount: '',
        expense_type: 'operational',
        receipt_url: '',
        approval_status: 'pending'
      });
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try {
      // Record an actual payment — the backend still expects a `due_date`
      // field (kept for backwards compatibility); we send the actual
      // payment_date as both so existing reports keep working.
      const isoPaymentDate = new Date(paymentFormData.payment_date).toISOString();
      const paymentData = {
        ...paymentFormData,
        amount: parseFloat(paymentFormData.amount),
        due_date: isoPaymentDate,
        payment_date: isoPaymentDate,
        status: paymentFormData.status || 'paid',
      };

      const token = localStorage.getItem('token');
      
      if (editingPayment) {
        // Update existing payment
        await axios.put(`${API}/projects/${projectId}/payments/${editingPayment}`, paymentData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Payment updated successfully!');
        setEditingPayment(null);
      } else {
        // Record new payment
        await axios.post(`${API}/projects/${projectId}/payments`, paymentData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Payment recorded successfully!');
      }
      
      setShowPaymentDialog(false);
      setPaymentFormData({
        expense_id: '',
        recipient: '',
        amount: '',
        description: '',
        payment_method: 'bank_transfer',
        payment_date: todayISO,
        status: 'paid'
      });
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleCreateBudgetCategory = async (e) => {
    e.preventDefault();
    try {
      const budgetData = {
        ...budgetFormData,
        allocated_amount: parseFloat(budgetFormData.allocated_amount)
      };

      const token = localStorage.getItem('token');
      
      if (editingCategory) {
        // Update existing category
        await axios.put(`${API}/projects/${projectId}/budget-categories/${editingCategory}`, budgetData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (user.role === 'social_actor') {
          toast.success('Budget category edit submitted for approval!');
        } else {
          toast.success('Budget category updated successfully!');
        }
        setEditingCategory(null);
      } else {
        // Create new category
        await axios.post(`${API}/projects/${projectId}/budget-categories`, budgetData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (user.role === 'social_actor') {
          toast.success('Budget category submitted for approval!');
        } else {
          toast.success('Budget category created successfully!');
        }
      }
      
      setShowBudgetDialog(false);
      setBudgetFormData({
        category: '',
        allocated_amount: '',
        period: 'monthly',
        description: ''
      });
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save budget category');
    }
  };

  const handleDeleteBudgetCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this budget category?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/projects/${projectId}/budget-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (user.role === 'social_actor') {
        toast.success('Budget category deletion submitted for approval');
      } else {
        toast.success('Budget category deleted successfully');
      }
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete budget category');
    }
  };

  const handleApproveBudgetCategory = async (categoryId, actionType, approved) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/projects/${projectId}/budget-categories/${categoryId}/approve`, {
        approved: approved,
        action_type: actionType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Budget category ${actionType} ${approved ? 'approved' : 'rejected'} successfully`);
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const approveExpense = async (expenseId) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/expenses/${expenseId}`, {
        approval_status: 'approved'
      });
      toast.success('Expense approved successfully');
      fetchFinancialData();
    } catch (error) {
      toast.error('Failed to approve expense');
    }
  };

  const processPayment = async (paymentId) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/payments/${paymentId}`, {
        status: 'completed'
      });
      toast.success('Payment processed successfully');
      fetchFinancialData();
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/projects/${projectId}/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense deleted successfully');
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/projects/${projectId}/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment deleted successfully');
      fetchFinancialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  // Expense Approval Handler
  const handleExpenseApproval = async (approved) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/projects/${projectId}/expenses/${approvingExpense.id}/approve-edit`,
        { approved, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approved ? 'Expense edit approved!' : 'Expense edit rejected');
      setShowExpenseApprovalDialog(false);
      setApprovingExpense(null);
      setApprovalNotes('');
      fetchFinancialData();
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };
  
  // Payment Approval Handler
  const handlePaymentApproval = async (approved) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/projects/${projectId}/payments/${approvingPayment.id}/approve-edit`,
        { approved, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approved ? 'Payment edit approved!' : 'Payment edit rejected');
      setShowPaymentApprovalDialog(false);
      setApprovingPayment(null);
      setApprovalNotes('');
      fetchFinancialData();
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  
  // Bulk Approval Handler
  const handleBulkApproval = async (approved) => {
    try {
      const selectedItems = bulkApprovalType === 'expenses' ? selectedExpenses : selectedPayments;
      
      if (selectedItems.length === 0) {
        toast.error('No items selected');
        return;
      }
      
      const items = selectedItems.map(itemId => ({
        id: itemId,
        approved: approved,
        notes: bulkApprovalNotes
      }));
      
      const response = await axios.post(
        `${API}/projects/${projectId}/bulk-approve-edits`,
        {
          entity_type: bulkApprovalType,
          items: items,
          common_notes: bulkApprovalNotes
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(
        `Bulk ${approved ? 'approval' : 'rejection'} complete! ` +
        `${response.data.successful} successful, ${response.data.failed} failed`
      );
      
      setShowBulkApprovalDialog(false);
      setBulkApprovalNotes('');
      setSelectedExpenses([]);
      setSelectedPayments([]);
      fetchFinancialData();
    } catch (error) {
      console.error('Failed to process bulk approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk approval');
    }
  };
  
  // Toggle selection
  const toggleExpenseSelection = (expenseId) => {
    setSelectedExpenses(prev => 
      prev.includes(expenseId) ? prev.filter(id => id !== expenseId) : [...prev, expenseId]
    );
  };
  
  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) ? prev.filter(id => id !== paymentId) : [...prev, paymentId]
    );
  };



  const getExpenseStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend = null }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-5 w-5 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center mt-2 text-xs ${trend.positive ? 'text-green-700' : 'text-red-600'}`}>
            {trend.positive ? 
              <TrendingUp className="h-3 w-3 mr-1" /> : 
              <TrendingDown className="h-3 w-3 mr-1" />
            }
            <span>{trend.value}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-green-700" />
            <span>Financial Management Suite</span>
          </h3>
          <p className="text-sm text-gray-600">Budget tracking, expense management, and financial reporting</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current_month">Current Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Financial Overview */}
      {budget && financialReports && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Budget"
            value={formatCurrency(budget.total_budget)}
            subtitle="Project allocation"
            icon={Target}
            color="blue"
          />
          <MetricCard
            title="Total Expenses"
            value={formatCurrency(financialReports.totalExpenses)}
            subtitle={`${financialReports.budgetUtilization}% utilized`}
            icon={Receipt}
            color="orange"
            trend={{ 
              positive: financialReports.budgetUtilization < 80, 
              value: `${financialReports.budgetUtilization}% of budget`
            }}
          />
          <MetricCard
            title="Remaining Budget"
            value={formatCurrency(financialReports.remainingBudget)}
            subtitle="Available funds"
            icon={Wallet}
            color="green"
          />
          <MetricCard
            title="Pending Payments"
            value={formatCurrency(financialReports.totalPayments)}
            subtitle={`${payments.filter(p => p.status === 'pending').length} transactions`}
            icon={Clock}
            color="purple"
          />
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="certification" data-testid="tab-certification">Certification</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Budget Utilization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5 text-blue-600" />
                  <span>Budget Utilization</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Overall Progress</span>
                    <span className="text-sm text-gray-600">{financialReports.budgetUtilization}%</span>
                  </div>
                  <Progress value={parseFloat(financialReports.budgetUtilization)} className="h-3" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(financialReports.remainingBudget)}
                    </p>
                    <p className="text-sm text-gray-600">Remaining Budget</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expense Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-green-700" />
                  <span>Expenses by Category</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {financialReports.expensesByCategory?.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">{item.category}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 bg-green-700 rounded-full"
                            style={{ 
                              width: `${(item.amount / Math.max(...financialReports.expensesByCategory.map(c => c.amount))) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{formatCurrency(item.amount)}</span>
                      </div>
                    </div>
                  )) || (
                    <p className="text-gray-500 text-center py-4">No expense data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-purple-600" />
                <span>Recent Financial Activity</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...expenses.slice(0, 3), ...payments.slice(0, 2)]
                  .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                  .slice(0, 5)
                  .map((transaction, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${transaction.category ? 'bg-orange-100' : 'bg-blue-100'}`}>
                          {transaction.category ? 
                            <Receipt className="h-4 w-4 text-orange-700" /> : 
                            <CreditCard className="h-4 w-4 text-blue-600" />
                          }
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {transaction.description || transaction.recipient}
                          </p>
                          <p className="text-xs text-gray-500">
                            {transaction.category ? 'Expense' : 'Payment'} • {
                              new Date(transaction.date || transaction.created_at).toLocaleDateString('en-GB')
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(transaction.amount)}</p>
                        <Badge className={transaction.category ? 
                          getExpenseStatusColor(transaction.approval_status) : 
                          getPaymentStatusColor(transaction.status)
                        }>
                          {transaction.approval_status || transaction.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                }
                {expenses.length === 0 && payments.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No recent financial activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Expense Management</h4>
            <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-expense-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Record Expense
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record New Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateExpense} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expense-category">Budget Category</Label>
                      <Select
                        value={expenseFormData.budget_category_id}
                        onValueChange={(value) => {
                          const cat = budgetCategories.find((c) => c.id === value);
                          setExpenseFormData({
                            ...expenseFormData,
                            budget_category_id: value,
                            category: (cat?.name || cat?.category || '').toLowerCase(),
                          });
                        }}
                      >
                        <SelectTrigger data-testid="expense-category-select">
                          <SelectValue placeholder="Select a budget category" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetCategories.map((c) => {
                            const label = c.name || c.category || 'Unnamed';
                            const alloc = Number(c.allocated_amount || 0);
                            return (
                              <SelectItem key={c.id} value={c.id}>
                                {label} · €{alloc.toLocaleString('el-GR')}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Required. Expenses must be linked to a budget category and cannot exceed its remaining allocation.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expense-amount">Amount</Label>
                      <Input
                        id="expense-amount"
                        type="number"
                        step="0.01"
                        value={expenseFormData.amount}
                        onChange={(e) => setExpenseFormData({...expenseFormData, amount: e.target.value})}
                        placeholder="0.00"
                        required
                        data-testid="expense-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expense-description">Description</Label>
                    <Textarea
                      id="expense-description"
                      value={expenseFormData.description}
                      onChange={(e) => setExpenseFormData({...expenseFormData, description: e.target.value})}
                      placeholder="Describe the expense..."
                      rows={3}
                      required
                      data-testid="expense-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expense-type">Expense Type</Label>
                    <Select value={expenseFormData.expense_type} onValueChange={(value) => 
                      setExpenseFormData({...expenseFormData, expense_type: value})
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operational">Operational</SelectItem>
                        <SelectItem value="capital">Capital</SelectItem>
                        <SelectItem value="administrative">Administrative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-expense-submit">Record Expense</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>



          {/* Bulk Actions Toolbar for Expenses */}
          {(user.role === 'project_manager' || user.role === 'administrator') && 
           expenses.some(e => e.edit_status === 'pending_approval') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <p className="text-sm font-medium text-blue-900">
                    {expenses.filter(e => e.edit_status === 'pending_approval').length} Expense{expenses.filter(e => e.edit_status === 'pending_approval').length !== 1 ? 's' : ''} pending approval
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const pendingExpenses = expenses.filter(e => e.edit_status === 'pending_approval').map(e => e.id);
                      setSelectedExpenses(pendingExpenses);
                    }}
                  >
                    Select All Pending
                  </Button>
                  {selectedExpenses.length > 0 && (
                    <span className="text-sm text-blue-700">
                      {selectedExpenses.length} selected
                    </span>
                  )}
                </div>
                {selectedExpenses.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setBulkApprovalType('expenses');
                        setShowBulkApprovalDialog(true);
                      }}
                      className="bg-green-700 hover:bg-green-800"
                    >
                      Bulk Approve ({selectedExpenses.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setBulkApprovalType('expenses');
                        setShowBulkApprovalDialog(true);
                      }}
                    >
                      Bulk Reject ({selectedExpenses.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedExpenses([])}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {expenses.map((expense) => {
              // Compute paid-so-far and remaining balance from the loaded
              // payments. Drives the inline progress bar and the
              // "Record payment" quick-action that pre-fills the dialog.
              // When the user is EDITING a payment, we exclude that
              // payment's previous amount from the paid-so-far total —
              // otherwise the remaining balance would double-count it
              // and look "wrong" (e.g. an already-fully-paid expense
              // shows 0 € remaining even though the user is about to
              // overwrite the very payment that fills it).
              const linkedPayments = (payments || []).filter(
                (p) => p.expense_id === expense.id
                       && !['cancelled', 'rejected', 'failed'].includes(p.status)
                       && p.id !== editingPayment
              );
              const paidSoFar = linkedPayments.reduce(
                (s, p) => s + (Number(p.amount) || 0), 0
              );
              const expAmt = Number(expense.amount) || 0;
              const remaining = Math.max(0, expAmt - paidSoFar);
              const pct = expAmt > 0 ? Math.min(100, Math.round((paidSoFar / expAmt) * 100)) : 0;
              const fullyPaid = remaining <= 0.01 && expAmt > 0;
              const canSchedule = (user.role === 'project_manager'
                                   || user.role === 'administrator'
                                   || expense.created_by === user.id);
              return (
              <Card key={expense.id} data-testid={`expense-card-${expense.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Checkbox for bulk selection */}
                      {(user.role === 'project_manager' || user.role === 'administrator') && 
                       expense.edit_status === 'pending_approval' && (
                        <input
                          type="checkbox"
                          checked={selectedExpenses.includes(expense.id)}
                          onChange={() => toggleExpenseSelection(expense.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      )}
                      <div className="p-2 rounded-full bg-orange-100">
                        <Receipt className="h-5 w-5 text-orange-700" />
                      </div>
                      <div>
                        <h4 className="font-medium">{expense.description}</h4>
                        <p className="text-sm text-gray-500 capitalize">
                          {expense.category} • {expense.expense_type}
                        </p>
                        <p className="text-xs text-gray-500">
                          {expense.date ? new Date(expense.date).toLocaleDateString('en-GB') : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold">{formatCurrency(expense.amount)}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge className={getExpenseStatusColor(expense.approval_status)}>
                            {expense.approval_status}
                          </Badge>
                          {/* Edit Approval Status Badge */}
                          {expense.edit_status === 'pending_approval' && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                              Edit Pending
                            </Badge>
                          )}
                          {expense.edit_status === 'rejected' && (
                            <Badge variant="destructive">
                              Edit Rejected
                            </Badge>
                          )}
                          {/* Review Button for PM/Admin */}
                          {(user.role === 'project_manager' || user.role === 'administrator') && 
                           expense.edit_status === 'pending_approval' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setApprovingExpense(expense);
                                setShowExpenseApprovalDialog(true);
                              }}
                              className="text-green-700 hover:text-green-800"
                            >
                              Review Edit
                            </Button>
                          )}
                          {expense.approval_status === 'pending' && user.role === 'administrator' && (
                            <Button 
                              size="sm" 
                              onClick={() => approveExpense(expense.id)}
                              data-testid={`approve-expense-${expense.id}`}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Edit/Delete buttons - show to creator or admin/PM */}
                      {(expense.created_by === user.id || user.role === 'administrator' || user.role === 'project_manager') && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit Expense"
                            onClick={() => {
                              setEditingExpense(expense.id);
                              setExpenseFormData({
                                category: expense.category,
                                description: expense.description,
                                amount: expense.amount.toString(),
                                expense_type: expense.expense_type,
                                receipt_url: expense.receipt_url || '',
                                approval_status: expense.approval_status
                              });
                              setShowExpenseDialog(true);
                            }}
                          >
                            <Edit3 className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Delete Expense"
                            onClick={() => handleDeleteExpense(expense.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment chain progress bar + Schedule-payment quick action */}
                  {expAmt > 0 && (
                    <div className="mt-3 pl-12">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600">
                          Paid <span className="font-semibold text-emerald-700">{formatCurrency(paidSoFar)}</span>
                          {' '}of {formatCurrency(expAmt)}
                          {!fullyPaid && (
                            <span className="text-gray-500"> · {formatCurrency(remaining)} remaining</span>
                          )}
                          {linkedPayments.length > 0 && (
                            <span className="text-gray-500"> · {linkedPayments.length} payment(s)</span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${fullyPaid ? 'text-emerald-700' : pct > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                            {pct}%
                          </span>
                          {fullyPaid && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                              Fully paid
                            </Badge>
                          )}
                          {!fullyPaid && canSchedule && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                setPaymentFormData({
                                  expense_id: expense.id,
                                  recipient: '',
                                  amount: String(remaining),
                                  description: expense.description || '',
                                  payment_method: 'bank_transfer',
                                  payment_date: todayISO,
                                  status: 'paid',
                                });
                                setShowPaymentDialog(true);
                              }}
                              data-testid={`record-payment-${expense.id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" /> Record payment
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full transition-all ${fullyPaid ? 'bg-emerald-700' : 'bg-gradient-to-r from-blue-500 to-emerald-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
            {expenses.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No expenses recorded yet</p>
                <p className="text-sm">Record expenses to track project spending</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Payment Management</h4>
            <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-payment-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePayment} className="space-y-4">
                  {/* Expense link — required so payments stay traceable
                      back to a budgeted expense per the financial-integrity rules. */}
                  <div className="space-y-2">
                    <Label htmlFor="payment-expense">Expense</Label>
                    <Select
                      value={paymentFormData.expense_id}
                      onValueChange={(value) => {
                        const exp = expenses.find((e) => e.id === value);
                        setPaymentFormData({
                          ...paymentFormData,
                          expense_id: value,
                          // Pre-fill amount with the remaining balance on that expense
                          // (UI hint only — backend re-validates capacity).
                          amount: exp ? String(exp.amount) : paymentFormData.amount,
                          description: paymentFormData.description || exp?.description || '',
                        });
                      }}
                    >
                      <SelectTrigger data-testid="payment-expense-select">
                        <SelectValue placeholder="Select the expense to settle" />
                      </SelectTrigger>
                      <SelectContent>
                        {expenses.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {(e.description || 'Expense').slice(0, 60)} · €{Number(e.amount || 0).toLocaleString('el-GR')}
                            {e.budget_category_name ? ` · ${e.budget_category_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Required. Payments must settle an existing expense and cannot exceed its remaining balance.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment-recipient">Recipient</Label>
                      <Input
                        id="payment-recipient"
                        value={paymentFormData.recipient}
                        onChange={(e) => setPaymentFormData({...paymentFormData, recipient: e.target.value})}
                        placeholder="e.g., Vendor Name"
                        required
                        data-testid="payment-recipient-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment-amount">Amount</Label>
                      <Input
                        id="payment-amount"
                        type="number"
                        step="0.01"
                        value={paymentFormData.amount}
                        onChange={(e) => setPaymentFormData({...paymentFormData, amount: e.target.value})}
                        placeholder="0.00"
                        required
                        data-testid="payment-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment-description">Description</Label>
                    <Textarea
                      id="payment-description"
                      value={paymentFormData.description}
                      onChange={(e) => setPaymentFormData({...paymentFormData, description: e.target.value})}
                      placeholder="Payment purpose..."
                      rows={3}
                      required
                      data-testid="payment-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment-method">Payment Method</Label>
                      <Select value={paymentFormData.payment_method} onValueChange={(value) => 
                        setPaymentFormData({...paymentFormData, payment_method: value})
                      }>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment-payment-date">Payment Date</Label>
                      <Input
                        id="payment-payment-date"
                        type="date"
                        value={paymentFormData.payment_date}
                        onChange={(e) => setPaymentFormData({...paymentFormData, payment_date: e.target.value})}
                        required
                        data-testid="payment-date-input"
                      />
                      <p className="text-xs text-gray-500">
                        Date the payment was actually made. Defaults to today.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-payment-submit">{editingPayment ? 'Save Changes' : 'Record Payment'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>



          {/* Bulk Actions Toolbar for Payments */}
          {(user.role === 'project_manager' || user.role === 'administrator') && 
           payments.some(p => p.edit_status === 'pending_approval') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <p className="text-sm font-medium text-blue-900">
                    {payments.filter(p => p.edit_status === 'pending_approval').length} Payment{payments.filter(p => p.edit_status === 'pending_approval').length !== 1 ? 's' : ''} pending approval
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const pendingPayments = payments.filter(p => p.edit_status === 'pending_approval').map(p => p.id);
                      setSelectedPayments(pendingPayments);
                    }}
                  >
                    Select All Pending
                  </Button>
                  {selectedPayments.length > 0 && (
                    <span className="text-sm text-blue-700">
                      {selectedPayments.length} selected
                    </span>
                  )}
                </div>
                {selectedPayments.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setBulkApprovalType('payments');
                        setShowBulkApprovalDialog(true);
                      }}
                      className="bg-green-700 hover:bg-green-800"
                    >
                      Bulk Approve ({selectedPayments.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setBulkApprovalType('payments');
                        setShowBulkApprovalDialog(true);
                      }}
                    >
                      Bulk Reject ({selectedPayments.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedPayments([])}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.id} data-testid={`payment-card-${payment.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Checkbox for bulk selection */}
                      {(user.role === 'project_manager' || user.role === 'administrator') && 
                       payment.edit_status === 'pending_approval' && (
                        <input
                          type="checkbox"
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => togglePaymentSelection(payment.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      )}
                      <div className="p-2 rounded-full bg-blue-100">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{payment.recipient}</h4>
                        <p className="text-sm text-gray-500">{payment.description}</p>
                        <p className="text-xs text-gray-500 capitalize">
                          {(payment.payment_method || 'bank_transfer').replace('_', ' ')} • Paid: {(payment.payment_date || payment.due_date) ? new Date(payment.payment_date || payment.due_date).toLocaleDateString('en-GB') : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold">{formatCurrency(payment.amount)}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge className={getPaymentStatusColor(payment.status)}>
                            {payment.status}
                          </Badge>
                          {/* Edit Approval Status Badge */}
                          {payment.edit_status === 'pending_approval' && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                              Edit Pending
                            </Badge>
                          )}
                          {payment.edit_status === 'rejected' && (
                            <Badge variant="destructive">
                              Edit Rejected
                            </Badge>
                          )}
                          {/* Review Button for PM/Admin */}
                          {(user.role === 'project_manager' || user.role === 'administrator') && 
                           payment.edit_status === 'pending_approval' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setApprovingPayment(payment);
                                setShowPaymentApprovalDialog(true);
                              }}
                              className="text-green-700 hover:text-green-800"
                            >
                              Review Edit
                            </Button>
                          )}
                          {payment.status === 'pending' && user.role === 'administrator' && (
                            <Button 
                              size="sm" 
                              onClick={() => processPayment(payment.id)}
                              data-testid={`process-payment-${payment.id}`}
                            >
                              Process
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Edit/Delete buttons - show to creator or admin/PM */}
                      {(payment.created_by === user.id || user.role === 'administrator' || user.role === 'project_manager') && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit Payment"
                            onClick={() => {
                              setEditingPayment(payment.id);
                              setPaymentFormData({
                                expense_id: payment.expense_id || '',
                                recipient: payment.recipient,
                                amount: payment.amount.toString(),
                                description: payment.description,
                                payment_method: payment.payment_method,
                                payment_date: (payment.payment_date || payment.due_date) ? (payment.payment_date || payment.due_date).split('T')[0] : todayISO,
                                status: payment.status || 'paid'
                              });
                              setShowPaymentDialog(true);
                            }}
                          >
                            <Edit3 className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Delete Payment"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {payments.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No payments scheduled yet</p>
                <p className="text-sm">Schedule payments to manage cash flow</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Budget Management</h4>
            <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-budget-category-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Budget Category</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateBudgetCategory} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="budget-category">Category Name</Label>
                      <Input
                        id="budget-category"
                        value={budgetFormData.category}
                        onChange={(e) => setBudgetFormData({...budgetFormData, category: e.target.value})}
                        placeholder="e.g., Training Materials"
                        required
                        data-testid="budget-category-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budget-amount">Allocated Amount</Label>
                      <Input
                        id="budget-amount"
                        type="number"
                        step="0.01"
                        value={budgetFormData.allocated_amount}
                        onChange={(e) => setBudgetFormData({...budgetFormData, allocated_amount: e.target.value})}
                        placeholder="0.00"
                        required
                        data-testid="budget-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget-description">Description</Label>
                    <Textarea
                      id="budget-description"
                      value={budgetFormData.description}
                      onChange={(e) => setBudgetFormData({...budgetFormData, description: e.target.value})}
                      placeholder="Budget category description..."
                      rows={3}
                      data-testid="budget-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget-period">Period</Label>
                    <Select value={budgetFormData.period} onValueChange={(value) => 
                      setBudgetFormData({...budgetFormData, period: value})
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="one_time">Once (one-time payment)</SelectItem>
                        <SelectItem value="project">Project Duration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowBudgetDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-budget-category-submit">Create Category</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgetCategories.map((category) => {
              const spent = financialReports.expensesByCategory?.find(
                exp => exp.category === category.category
              )?.amount || 0;
              const utilization = category.allocated_amount > 0 ? 
                ((spent / category.allocated_amount) * 100).toFixed(1) : 0;
              
              return (
                <Card key={category.id} data-testid={`budget-category-${category.id}`}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium capitalize">{category.category}</h4>
                            <div className="flex items-center gap-2">
                              <Badge className={utilization > 100 ? 'bg-red-100 text-red-800' : 
                                utilization > 80 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                              }>
                                {utilization}%
                              </Badge>
                              {/* Status badges for pending approvals */}
                              {category.approval_status === 'pending_approval' && (
                                <Badge className="bg-yellow-100 text-yellow-800">
                                  Pending Approval
                                </Badge>
                              )}
                              {category.edit_status === 'pending_approval' && (
                                <Badge className="bg-purple-100 text-purple-800">
                                  Edit Pending
                                </Badge>
                              )}
                              {category.delete_status === 'pending_deletion' && (
                                <Badge className="bg-red-100 text-red-800">
                                  Delete Pending
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Edit/Delete buttons for Social Actors, PM, and Admin */}
                        {(user.role === 'social_actor' || user.role === 'project_manager' || user.role === 'administrator') && 
                         category.approval_status !== 'pending_approval' && 
                         category.delete_status !== 'pending_deletion' && (
                          <div className="flex gap-1 ml-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Edit Category"
                              onClick={() => {
                                setEditingCategory(category.id);
                                setBudgetFormData({
                                  category: category.category,
                                  allocated_amount: category.allocated_amount.toString(),
                                  description: category.description || ''
                                });
                                setShowBudgetDialog(true);
                              }}
                            >
                              <Edit3 className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Delete Category"
                              onClick={() => handleDeleteBudgetCategory(category.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                        {/* Approve/Reject buttons for PM and Admin */}
                        {(user.role === 'project_manager' || user.role === 'administrator') && (
                          <>
                            {category.approval_status === 'pending_approval' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveBudgetCategory(category.id, 'create', true)}
                                className="bg-green-700 hover:bg-green-800 text-white ml-2"
                              >
                                Approve
                              </Button>
                            )}
                            {category.edit_status === 'pending_approval' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveBudgetCategory(category.id, 'edit', true)}
                                className="bg-purple-600 hover:bg-purple-700 text-white ml-2"
                              >
                                Approve Edit
                              </Button>
                            )}
                            {category.delete_status === 'pending_deletion' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveBudgetCategory(category.id, 'delete', true)}
                                className="bg-red-600 hover:bg-red-700 text-white ml-2"
                              >
                                Approve Delete
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{category.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Allocated:</span>
                          <span className="font-medium">{formatCurrency(category.allocated_amount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Spent:</span>
                          <span className="font-medium">{formatCurrency(spent)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Remaining:</span>
                          <span className={`font-medium ${category.allocated_amount - spent < 0 ? 'text-red-600' : 'text-green-700'}`}>
                            {formatCurrency(category.allocated_amount - spent)}
                          </span>
                        </div>
                        <Progress value={Math.min(utilization, 100)} className="h-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {budgetCategories.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <Calculator className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No budget categories defined yet</p>
                <p className="text-sm">Create budget categories to track spending</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Financial Reports</h4>
            <div className="flex items-center space-x-2">
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-28" data-testid="export-financial-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="xlsx">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={exportFinancialReport}
                disabled={exporting}
                data-testid="export-financial-report"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting…' : 'Export Report'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Spending Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <span>Monthly Spending Trend</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-gray-500">
                  📈 Monthly spending visualization
                  <br />
                  <small>(Chart would show spending trends over time)</small>
                </div>
              </CardContent>
            </Card>

            {/* Cost Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5 text-green-700" />
                  <span>Cost Analysis</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(financialReports.totalExpenses)}
                    </p>
                    <p className="text-sm text-gray-600">Total Project Costs</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Average Monthly Spend:</span>
                      <span className="font-medium">
                        {formatCurrency(financialReports.totalExpenses / Math.max(1, financialReports.monthlyTrend?.length || 1))}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Projected Annual Cost:</span>
                      <span className="font-medium">
                        {formatCurrency(financialReports.totalExpenses * 12 / Math.max(1, financialReports.monthlyTrend?.length || 1))}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <span>Financial Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {formatCurrency(budget?.total_budget || 0)}
                  </div>
                  <p className="text-sm text-gray-600">Total Budget</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-700">
                    {formatCurrency(financialReports.totalExpenses)}
                  </div>
                  <p className="text-sm text-gray-600">Total Expenses</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-700">
                    {financialReports.budgetUtilization}%
                  </div>
                  <p className="text-sm text-gray-600">Budget Utilization</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certification" className="space-y-6">
          <CertificationTracking projectId={projectId} />
        </TabsContent>
      </Tabs>
      <Dialog open={showExpenseApprovalDialog} onOpenChange={setShowExpenseApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Expense Edit</DialogTitle>
          </DialogHeader>
          {approvingExpense && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Expense: {approvingExpense.description}</h3>
                {approvingExpense.pending_changes && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Pending Changes:</p>
                    <div className="bg-white p-3 rounded border">
                      <pre className="text-sm whitespace-pre-wrap">
                        {JSON.stringify(approvingExpense.pending_changes, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Review Notes</Label>
                <Textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add notes about this approval/rejection..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowExpenseApprovalDialog(false);
                    setApprovingExpense(null);
                    setApprovalNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleExpenseApproval(false)}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handleExpenseApproval(true)}
                  className="bg-green-700 hover:bg-green-800"
                >
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Approval Dialog */}
      <Dialog open={showPaymentApprovalDialog} onOpenChange={setShowPaymentApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Payment Edit</DialogTitle>
          </DialogHeader>
          {approvingPayment && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Payment to: {approvingPayment.recipient}</h3>
                {approvingPayment.pending_changes && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Pending Changes:</p>
                    <div className="bg-white p-3 rounded border">
                      <pre className="text-sm whitespace-pre-wrap">
                        {JSON.stringify(approvingPayment.pending_changes, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Review Notes</Label>
                <Textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add notes about this approval/rejection..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPaymentApprovalDialog(false);
                    setApprovingPayment(null);
                    setApprovalNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handlePaymentApproval(false)}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handlePaymentApproval(true)}
                  className="bg-green-700 hover:bg-green-800"
                >
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Bulk Approval Dialog */}
      <Dialog open={showBulkApprovalDialog} onOpenChange={setShowBulkApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Approval - {bulkApprovalType}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">
                You are about to process {bulkApprovalType === 'expenses' ? selectedExpenses.length : selectedPayments.length} items
              </p>
              <p className="text-sm text-blue-700">
                Choose "Approve All" to accept all selected edits or "Reject All" to decline them.
              </p>
            </div>
            <div>
              <Label>Common Review Notes (Optional)</Label>
              <Textarea
                value={bulkApprovalNotes}
                onChange={(e) => setBulkApprovalNotes(e.target.value)}
                placeholder="Add notes that will be applied to all selected items..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkApprovalDialog(false);
                  setBulkApprovalNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleBulkApproval(false)}
              >
                Reject All
              </Button>
              <Button
                onClick={() => handleBulkApproval(true)}
                className="bg-green-700 hover:bg-green-800"
              >
                Approve All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default FinancialManagement;